import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import toast from '../../utils/toast';

const PrefillingSettings = () => {
  const modalContentRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [prefillings, setPrefillings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    taskName: '',
    description: '',
    client: '',
    assignedTo: [],
    assignedGroups: [],
    deadlineDays: '',
    status: 'pending',
    verificationTypes: []
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);

  useEffect(() => {
    fetchInitialData();
    fetchPrefillings();
  }, []);

  useEffect(() => {
    if (!showModal) return;
    const id = requestAnimationFrame(() => {
      const el = modalContentRef.current?.querySelector('input:not([type="hidden"]), textarea, select');
      if (el) el.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [showModal]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = clients.filter(client =>
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredClients([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, clients]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [clientsRes, usersRes, groupsRes, verificationTypesRes] = await Promise.all([
        api.get('/master-data'),
        api.get('/users'),
        api.get('/groups'),
        api.get('/admin/verification-types')
      ]);
      if (clientsRes.data.success) {
        setClients(clientsRes.data.data || []);
      } else {
        setClients(clientsRes.data || []);
      }
      setUsers(usersRes.data.data || []);
      setGroups(groupsRes.data || []);
      setVerificationTypes(verificationTypesRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch initial data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrefillings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/task-prefillings');
      setPrefillings(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      toast.error('Failed to fetch task prefillings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setFormData({
      name: '',
      taskName: '',
      description: '',
      client: '',
      assignedTo: [],
      assignedGroups: [],
      deadlineDays: '',
      status: 'pending',
      verificationTypes: []
    });
    setSearchTerm('');
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setFormData({
      name: p.name,
      taskName: p.taskName || '',
      description: p.description || '',
      client: p.client?._id || '',
      assignedTo: (p.assignedTo || []).map(u => u._id),
      assignedGroups: (p.assignedGroups || []).map(g => g._id),
      deadlineDays: p.deadlineDays != null ? p.deadlineDays : '',
      status: p.status || 'pending',
      verificationTypes: (p.verificationTypes || []).map(v => v._id)
    });
    setSearchTerm(p.client?.clientName || '');
    setShowModal(true);
  };

  const handleClientSelect = (clientId) => {
    setFormData(prev => ({ ...prev, client: clientId }));
    const c = clients.find(x => x._id === clientId);
    setSearchTerm(c ? c.clientName : '');
    setShowSuggestions(false);
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(userId)
        ? prev.assignedTo.filter(id => id !== userId)
        : [...prev.assignedTo, userId]
    }));
  };

  const toggleGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      assignedGroups: prev.assignedGroups.includes(groupId)
        ? prev.assignedGroups.filter(id => id !== groupId)
        : [...prev.assignedGroups, groupId]
    }));
  };

  const toggleVerificationType = (vtId) => {
    setFormData(prev => ({
      ...prev,
      verificationTypes: prev.verificationTypes.includes(vtId)
        ? prev.verificationTypes.filter(id => id !== vtId)
        : [...prev.verificationTypes, vtId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        taskName: formData.taskName,
        description: formData.description,
        client: formData.client || null,
        assignedTo: formData.assignedTo,
        assignedGroups: formData.assignedGroups,
        deadlineDays: formData.deadlineDays === '' ? null : Number(formData.deadlineDays),
        status: formData.status,
        verificationTypes: formData.verificationTypes
      };
      if (editing) {
        await api.put(`/admin/task-prefillings/${editing._id}`, payload);
        toast.success('Task prefilling updated');
      } else {
        await api.post('/admin/task-prefillings', payload);
        toast.success('Task prefilling created');
      }
      setShowModal(false);
      fetchPrefillings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save task prefilling');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task prefilling?')) return;
    try {
      await api.delete(`/admin/task-prefillings/${id}`);
      toast.success('Task prefilling deleted');
      fetchPrefillings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const formatAssignees = (p) => {
    const u = (p.assignedTo || []).map(x => (x.firstName && x.lastName) ? `${x.firstName} ${x.lastName}` : x).join(', ');
    const g = (p.assignedGroups || []).map(x => x.name || x).join(', ');
    return [u, g].filter(Boolean).join('; ') || '—';
  };

  if (loading && prefillings.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Prefilling Settings</h1>
          <p className="text-gray-600 mt-2">
            Create prefilling presets that users can select when creating new tasks.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    Create one or more task prefillings. When creating a new task (New/Tasks or View & Search/Tasks/Create Task), users can choose a prefilling from a dropdown to pre-fill the form.
                  </p>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Task prefillings</h2>
                  <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Add prefilling
                  </button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assign to</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline (days)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {prefillings.map((p) => (
                        <tr key={p._id}>
                          <td className="px-4 py-3 text-sm text-gray-900">{p.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{p.taskName || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{p.client?.clientName || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{formatAssignees(p)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{p.deadlineDays != null ? p.deadlineDays : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{p.status || '—'}</td>
                          <td className="px-4 py-3 text-right text-sm">
                            <button type="button" onClick={() => openEdit(p)} className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                            <button type="button" onClick={() => handleDelete(p._id)} className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {prefillings.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No task prefillings yet. Add one to offer when creating tasks.</p>
                  )}
                </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-0" onClick={() => setShowModal(false)} aria-hidden="true" />
            <div ref={modalContentRef} className="relative z-10 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit} className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{editing ? 'Edit Task Prefilling' : 'Add Task Prefilling'}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Prefilling name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Weekly Review, Onboarding"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">This name appears in the dropdown when creating a task.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default task name</label>
                    <input
                      type="text"
                      value={formData.taskName}
                      onChange={e => setFormData(prev => ({ ...prev, taskName: e.target.value }))}
                      placeholder="Optional default task name"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      placeholder="Optional default description"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default client</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Search client (optional)"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    />
                    {showSuggestions && filteredClients.length > 0 && (
                      <div className="mt-1 border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                        {filteredClients.map(c => (
                          <div
                            key={c._id}
                            onClick={() => handleClientSelect(c._id)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          >
                            {c.clientName}
                          </div>
                        ))}
                      </div>
                    )}
                    {formData.client && (
                      <p className="mt-1 text-xs text-gray-600">
                        Selected: {clients.find(c => c._id === formData.client)?.clientName}
                        <button type="button" onClick={() => { setFormData(prev => ({ ...prev, client: '' })); setSearchTerm(''); }} className="ml-2 text-blue-600">Clear</button>
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Assign to user(s)</label>
                    <div className="mt-1 border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                      {users.map(u => (
                        <label key={u._id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.assignedTo.includes(u._id)}
                            onChange={() => toggleUser(u._id)}
                            className="rounded border-gray-300"
                          />
                          <span className="ml-2 text-sm">{u.firstName} {u.lastName}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Assign to group(s)</label>
                    <div className="mt-1 border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                      {groups.map(g => (
                        <label key={g._id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.assignedGroups.includes(g._id)}
                            onChange={() => toggleGroup(g._id)}
                            className="rounded border-gray-300"
                          />
                          <span className="ml-2 text-sm">{g.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Deadline (days from creation)</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.deadlineDays}
                      onChange={e => setFormData(prev => ({ ...prev, deadlineDays: e.target.value }))}
                      placeholder="Leave empty for no default"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Verification type(s)</label>
                    <div className="mt-1 border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                      {verificationTypes.map(vt => (
                        <label key={vt._id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.verificationTypes.includes(vt._id)}
                            onChange={() => toggleVerificationType(vt._id)}
                            className="rounded border-gray-300"
                          />
                          <span className="ml-2 text-sm">{vt.label || vt.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrefillingSettings;
